import React from "react";
import styled from "@emotion/styled";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useBreakpoint } from "@avenue-8/sidekick-ui";

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Main = styled.div<{ open: boolean; drawerWidth: string | null }>`
  min-width: 0;
  transition: width 0.3s ease-in-out;
  width: ${({ open, drawerWidth }) =>
    open
      ? `calc(100% - ${drawerWidth})`
      : !drawerWidth
        ? "100%"
        : `calc(100% - ${drawerWidth === "0" ? "0" : "4rem"})`};
`;

const CustomDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    flex-shrink: 0;
    border-left: 1px solid ${(p) => p.theme.colors.border2};
  }
`;

const SmallTopDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    display: grid;
    grid-template-rows: 4rem 1fr;
    max-width: 4rem;
    flex-shrink: 0;
    border-left: 1px solid ${(p) => p.theme.colors.border2};
    background-color: ${(p) => p.theme.colors.background};
  }
`;

const SmallDrawerTopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 4rem;
  width: 100%;
  border-bottom: 1px solid ${(p) => p.theme.colors.border2};
  background-color: ${(p) => p.theme.colors.background};
`;

const SmallTopDrawerTitle = styled(Typography)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 2rem 0;
  writing-mode: vertical-rl;
  width: 100%;
  height: 100%;
  line-height: 100%;
`;

interface SplitScreenProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  title?: React.ReactNode;
  mainContent: React.ReactNode;
  splitContent: React.ReactNode;
  showDrawer?: boolean;
  drawerWidth: string;
}

export const SplitScreen = ({
  open,
  onClose: _onClose,
  onOpen,
  title,
  showDrawer,
  mainContent,
  splitContent,
  drawerWidth,
}: SplitScreenProps) => {
  const isSupported = !useBreakpoint("laptop");

  return (
    <Container id="split-screen-container">
      <Main
        open={open}
        drawerWidth={showDrawer && isSupported ? drawerWidth : null}
      >
        {mainContent}
      </Main>

      <SmallTopDrawer
        variant="persistent"
        anchor="right"
        open={!open}
        PaperProps={{
          sx: { width: showDrawer ? "100%" : 0, flexShrink: 0 },
        }}
        hidden={!isSupported}
      >
        <SmallDrawerTopBar>
          <IconButton onClick={onOpen}>
            {open ? <LastPageIcon /> : <FirstPageIcon />}
          </IconButton>
        </SmallDrawerTopBar>
        <SmallTopDrawerTitle variant="h6">{title}</SmallTopDrawerTitle>
      </SmallTopDrawer>

      <CustomDrawer
        variant="persistent"
        anchor="right"
        open={open}
        PaperProps={{
          sx: { width: showDrawer ? drawerWidth : 0 },
        }}
        hidden={!isSupported}
      >
        {splitContent}
      </CustomDrawer>
    </Container>
  );
};
